/**
 * CaseManagerAssignment Component
 * 
 * Allows staff to assign, change, or remove case managers from inquiries.
 * Displays the current assigned case manager and provides a dropdown to select from staff list.
 * 
 * Requirements: 4.2, 4.3, 4.4
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const LIST_STAFF = gql`
  query ListStaff {
    listStaff {
      id
      firstName
      lastName
      role
      email
    }
  }
`;

const ASSIGN_CASE_MANAGER_TO_INQUIRY = gql`
  mutation AssignCaseManagerToInquiry($inquiryId: ID!, $caseManagerId: ID) {
    assignCaseManagerToInquiry(inquiryId: $inquiryId, caseManagerId: $caseManagerId) {
      id
      assignedCaseManagerId
      assignedCaseManager {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  email: string;
}

interface ListStaffData {
  listStaff: StaffMember[];
}

interface CaseManager {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface CaseManagerAssignmentProps {
  inquiryId: string;
  currentAssignedCaseManager?: CaseManager | null;
  onAssignmentChange?: () => void;
}

export function CaseManagerAssignment({
  inquiryId,
  currentAssignedCaseManager,
  onAssignmentChange,
}: CaseManagerAssignmentProps) {
  const [selectedCaseManagerId, setSelectedCaseManagerId] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Query staff members
  const { data: staffData, loading: staffLoading } = useQuery<ListStaffData>(LIST_STAFF);

  // Mutation for assigning case manager
  const [assignCaseManager] = useMutation(ASSIGN_CASE_MANAGER_TO_INQUIRY);

  // Filter to only inquiry managers
  const caseManagers = (staffData?.listStaff || []).filter(
    (staff: any) => staff.role === 'INQUIRY_MANAGER'
  );

  // Initialize selected value when current assignment changes
  useEffect(() => {
    if (currentAssignedCaseManager) {
      setSelectedCaseManagerId(currentAssignedCaseManager.id);
    } else {
      setSelectedCaseManagerId('');
    }
  }, [currentAssignedCaseManager]);

  // Handle assignment
  const handleAssign = async () => {
    if (!selectedCaseManagerId) {
      return;
    }

    setIsAssigning(true);
    try {
      await assignCaseManager({
        variables: {
          inquiryId,
          caseManagerId: selectedCaseManagerId,
        },
      });
      onAssignmentChange?.();
    } catch (error: any) {
      console.error('Error assigning case manager:', error);
      alert('Error assigning case manager: ' + error.message);
    } finally {
      setIsAssigning(false);
    }
  };

  // Handle removal
  const handleRemove = async () => {
    setIsAssigning(true);
    try {
      await assignCaseManager({
        variables: {
          inquiryId,
          caseManagerId: null,
        },
      });
      setSelectedCaseManagerId('');
      onAssignmentChange?.();
    } catch (error: any) {
      console.error('Error removing case manager:', error);
      alert('Error removing case manager: ' + error.message);
    } finally {
      setIsAssigning(false);
    }
  };

  // Check if selection has changed from current assignment
  const hasChanges = currentAssignedCaseManager
    ? selectedCaseManagerId !== currentAssignedCaseManager.id
    : selectedCaseManagerId !== '';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Case Manager Assignment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Assignment Display */}
        {currentAssignedCaseManager && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-xs font-medium text-green-800 mb-1">Currently Assigned</p>
            <p className="text-sm font-semibold text-green-900">
              {currentAssignedCaseManager.firstName} {currentAssignedCaseManager.lastName}
            </p>
            <p className="text-xs text-green-700">{currentAssignedCaseManager.email}</p>
          </div>
        )}

        {!currentAssignedCaseManager && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              No case manager assigned. Assign one to enable client messaging.
            </p>
          </div>
        )}

        {/* Case Manager Selection */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-2">
            {currentAssignedCaseManager ? 'Change Case Manager' : 'Select Case Manager'}
          </label>
          {staffLoading ? (
            <div className="text-sm text-gray-500">Loading staff members...</div>
          ) : (
            <select
              value={selectedCaseManagerId}
              onChange={(e) => setSelectedCaseManagerId(e.target.value)}
              disabled={isAssigning}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Select case manager"
            >
              <option value="">Select a case manager...</option>
              {caseManagers.map((cm: any) => (
                <option key={cm.id} value={cm.id}>
                  {cm.firstName} {cm.lastName} ({cm.role})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={handleAssign}
            disabled={isAssigning || !hasChanges || !selectedCaseManagerId}
            className="flex-1"
          >
            {isAssigning ? 'Assigning...' : 'Assign'}
          </Button>
          {currentAssignedCaseManager && (
            <Button
              onClick={handleRemove}
              disabled={isAssigning}
              variant="outline"
              className="flex-1"
            >
              {isAssigning ? 'Removing...' : 'Remove'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
